/**
 * BlockAccountModal — заблокировать аккаунт целиком: человек не войдёт ни
 * по коду, ни по паролю, ни в одну клинику; все сессии гасятся сразу.
 * Отличается от «Удалить врача» — то закрывает только одну клинику.
 * Общая для раздела «Аккаунты» и вкладки «Сотрудники» карточки клиента.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {TriangleAlert} from 'lucide-react';

import {blockAccount} from '@services/accounts';
import {keys} from '@queries/keys';
import {Field} from '@components/FormKit';
import {ModalShell} from '@pages/Clients/ClientCard/ModalShell';

type Props = {
  /** Кого блокируем; null — модалка закрыта */
  account: {id: string; fullName: string} | null;
  onClose: () => void;
  onSaved: () => void;
};

export const BlockAccountModal = ({account, onClose, onSaved}: Props) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const open = account !== null;
  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setReason('');
  }

  const mutation = useMutation({
    mutationFn: blockAccount,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.accounts.all});
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onSaved();
    },
  });

  const handleSubmit = () => {
    if (account !== null) mutation.mutate({id: account.id, reason});
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Заблокировать аккаунт"
      subtitle={
        <>
          <span className="font-semibold text-foreground">{account?.fullName}</span> потеряет доступ
          ко всем клиникам сразу — ни по коду, ни по паролю.
        </>
      }
      submitLabel="Заблокировать"
      onSubmit={handleSubmit}
      submitDisabled={reason.trim() === ''}
      pending={mutation.isPending}
      error={mutation.isError ? 'Не удалось заблокировать. Попробуйте ещё раз' : null}
      danger
    >
      <div className="flex gap-2.5 rounded-[11px] border border-destructive/25 bg-destructive/5 p-[11px_13px]">
        <TriangleAlert className="mt-px size-[17px] shrink-0 text-destructive" strokeWidth={1.9} />
        <div className="text-xs leading-[1.45] text-skr-ink2">
          Все сессии гаснут сразу, открытый кабинет перестанет работать. Приёмы и платежи, которые
          вёл человек, остаются в истории.
        </div>
      </div>
      <Field label="Причина (видна в админке)">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 300))}
          placeholder="Например: уволен, передал доступ посторонним"
          rows={3}
          className="w-full resize-none rounded-[10px] border border-input bg-card p-[11px_13px] text-sm leading-[1.5] outline-none transition focus:border-primary focus:ring-[3px] focus:ring-primary/12"
        />
      </Field>
    </ModalShell>
  );
};
